import { createFileRoute, Link } from "@tanstack/react-router";
import { Printer } from "lucide-react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import {
  KpiCard,
  Panel,
  RiskBadge,
  StatusChip,
  VerdictBadge,
} from "@/lovable-demo/components/bits";
import { C1000Chart, CostForecastChart } from "@/lovable-demo/components/charts";
import { Button } from "@/components/ui/button";
import {
  blendedForecast,
  blendedSeries,
  portfolio,
  recordsForProgram,
  topFive,
} from "@/lovable-demo/lib/warranty/aggregate";
import {
  currentStage,
  fmtCompactCurrency,
  fmtCurrency,
  fmtDate,
  recordVerdict,
} from "@/lovable-demo/lib/warranty/calc";
import { exportPdf } from "@/lovable-demo/lib/warranty/export";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";
import { ManagementEscalations } from "@/lovable-demo/components/escalation-summaries";

export const Route = createFileRoute("/demo/executive")({
  head: () => ({
    meta: [
      { title: "Executive Warranty Summary — Broadview" },
      {
        name: "description",
        content:
          "One-page executive summary of warranty risk, exposure, governance compliance and closed-loop progress, ready for PDF export.",
      },
      { property: "og:title", content: "Executive Warranty Summary — Broadview" },
      {
        property: "og:description",
        content:
          "One-page executive summary of warranty risk, exposure, governance compliance and closed-loop progress, ready for PDF export.",
      },
    ],
  }),
  component: ExecutiveSummary,
});

function ExecutiveSummary() {
  const { data, activeProgramId } = useWarranty();
  const records = recordsForProgram(data, activeProgramId);
  const program = data.programs.find((p) => p.id === activeProgramId);
  const stats = portfolio(records);
  const five = topFive(records);
  const violations = records.reduce(
    (a, r) => a + r.clauses.filter((c) => c.verdict === "Violated").length,
    0,
  );
  const gaps = records.reduce((a, r) => a + r.clauses.filter((c) => c.verdict === "Gap").length, 0);

  return (
    <AppShell
      title="Executive Summary"
      subtitle={program ? `${program.code} · ${program.name}` : "All programs"}
      actions={
        <Button variant="outline" size="sm" onClick={exportPdf}>
          <Printer className="size-3.5" /> Export PDF
        </Button>
      }
    >
      <div className="space-y-4">
        <Panel title="Position" subtitle={`Prepared ${fmtDate(new Date().toISOString())}`}>
          <p className="text-sm leading-relaxed text-foreground/90">
            {stats.open} governed Problem Records are open across{" "}
            {program ? program.name : `${data.programs.length} programs`}, of which {stats.critical}{" "}
            carry critical field risk. Gross exposure stands at{" "}
            <span className="num font-semibold">{fmtCurrency(stats.exposure)}</span> with{" "}
            <span className="num font-semibold">{fmtCurrency(stats.actual)}</span> already incurred.
            Remaining exposure (total exposure minus actual cost to date) stands at{" "}
            <span className="num font-semibold text-success">{fmtCurrency(stats.npr)}</span>; every
            month of delay carries{" "}
            <span className="num font-semibold text-warning">{fmtCurrency(stats.delay)}</span>.{" "}
            {stats.gated} records are held at an evidence gate and cannot advance until required
            aggregate evidence is filed.
          </p>
        </Panel>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard
            label="Open records"
            value={String(stats.open)}
            hint={`${stats.closed} closed`}
          />
          <KpiCard label="Gross exposure" value={fmtCompactCurrency(stats.exposure)} tone="bad" />
          <KpiCard label="Remaining exposure" value={fmtCompactCurrency(stats.npr)} tone="good" />
          <KpiCard
            label="Clause findings"
            value={`${violations} / ${gaps}`}
            tone={violations > 0 ? "bad" : "warn"}
            hint="Violated / gap across IATF & ISO"
          />
        </div>

        <ManagementEscalations recordIds={records.map((r) => r.id)} />

        <div className="grid gap-4 xl:grid-cols-2">
          <Panel title="C/1000 trend" subtitle="Claims per 1,000 units in service">
            <C1000Chart
              data={program ? program.trend : blendedSeries(records, "c1000")}
              target={program?.c1000Target}
              height={220}
            />
          </Panel>
          <Panel
            title="Cost vs projected cost"
            subtitle="Month-over-month growth projection; not an AI or statistical model"
          >
            <CostForecastChart data={blendedForecast(records)} height={220} />
          </Panel>
        </div>

        <Panel title="Top 5 exposure" subtitle="Risk-ranked with governance position">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">#</th>
                  <th className="px-2 py-2 text-left font-medium">Record</th>
                  <th className="px-2 py-2 text-left font-medium">Risk</th>
                  <th className="px-2 py-2 text-left font-medium">Status</th>
                  <th className="px-2 py-2 text-left font-medium">Stage</th>
                  <th className="px-2 py-2 text-right font-medium">Exposure</th>
                  <th className="px-2 py-2 text-left font-medium">Effectiveness</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {five.map((r, i) => (
                  <tr key={r.id}>
                    <td className="num px-2 py-2.5 text-muted-foreground">{i + 1}</td>
                    <td className="px-2 py-2.5">
                      <Link
                        to="/demo/problem-record/$id"
                        params={{ id: r.id }}
                        className="hover:text-primary"
                      >
                        <span className="num block text-xs text-primary">{r.code}</span>
                        <span className="block max-w-[300px] truncate">{r.title}</span>
                      </Link>
                    </td>
                    <td className="px-2 py-2.5">
                      <RiskBadge risk={r.risk} score={r.riskScore} />
                    </td>
                    <td className="px-2 py-2.5">
                      <StatusChip status={r.status} />
                    </td>
                    <td className="num px-2 py-2.5 text-xs text-muted-foreground">
                      {currentStage(r.stages).index}/11
                    </td>
                    <td className="num px-2 py-2.5 text-right">
                      {fmtCompactCurrency(r.financial.exposure)}
                    </td>
                    <td className="px-2 py-2.5">
                      <VerdictBadge verdict={recordVerdict(r.financial)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Decisions requested" subtitle="What governance needs from the executive team">
          <ol className="list-decimal space-y-2 pl-5 text-sm text-foreground/90">
            <li>
              Approve capacity for the {stats.gated} evidence-gated records so corrective actions
              are not delayed past their breakpoint window.
            </li>
            <li>
              Endorse escalation on {violations} violated clause findings, all concentrated in early
              containment and problem-solving activity.
            </li>
            <li>
              Confirm the supplier recovery position of{" "}
              {fmtCurrency(stats.recoveryTarget - stats.recovered)} still outstanding against claims
              already submitted.
            </li>
            <li>
              Accept read-across prevention actions on shared platforms before further programs
              reach job 1.
            </li>
          </ol>
        </Panel>
      </div>
    </AppShell>
  );
}
